if (!customElements.get("m-slideshow")) {
  class MSlideshow extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.autoplay = this.dataset.autoplay === "true";
      this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed);
      this.showArrows = this.dataset.enableArrows === "true";
      this.showDots = this.dataset.enableDots === "true";
      this.adaptHeight = this.dataset.slideHeight === "adapt";
      this.id = this.dataset.id;
      this.sliderContainer = this.querySelector(".swiper-container");

      this.lastVideo = null;
      this.lastActive = null;
      this.timeout = null;
      this.initSlider();
      
      this.initStickyMobileHero();
    }

    disconnectedCallback() {
      if (this._onBlockSelect) {
        document.removeEventListener("shopify:block:select", this._onBlockSelect);
        this._onBlockSelect = null;
      }
      // Shopify Theme Editor can replace this section without a page reload.
      if (this._fixedHeroSchedule) {
        window.removeEventListener("scroll", this._fixedHeroSchedule);
        window.removeEventListener("resize", this._fixedHeroSchedule);
        window.removeEventListener("orientationchange", this._fixedHeroSchedule);
        if (window.visualViewport) {
          window.visualViewport.removeEventListener("resize", this._fixedHeroSchedule);
        }
        this._fixedHeroSchedule = null;
      }
      if (this._fixedHeroFrame != null) {
        cancelAnimationFrame(this._fixedHeroFrame);
        this._fixedHeroFrame = null;
      }
      if (this._fixedHeroReducedMotion) {
        if (this._fixedHeroReducedMotion.removeEventListener) {
          this._fixedHeroReducedMotion.removeEventListener(
            "change", this._fixedHeroMotionChange
          );
        } else {
          this._fixedHeroReducedMotion.removeListener(this._fixedHeroMotionChange);
        }
        this._fixedHeroReducedMotion = null;
      }
      this.querySelectorAll(".m-slide__bg picture").forEach((picture) => {
        picture.style.removeProperty("height");
        picture.style.removeProperty("transform");
      });
      this._fixedHeroItems = null;
      if (this._stickyHeroImage && this._stickyHeroReady) {
        this._stickyHeroImage.removeEventListener("load", this._stickyHeroReady);
      }
      if (this._stickyHeroSection) {
        this._stickyHeroSection.classList.remove("m-slider--sticky-ready");
        this._stickyHeroSection.style.removeProperty("--m-sticky-mobile-height");
      }
      this._stickyHeroImage = null;
      if (this._stickyMobileQuery && this._stickyHeroBreakpointChange) {
        if (this._stickyMobileQuery.removeEventListener) {
          this._stickyMobileQuery.removeEventListener("change", this._stickyHeroBreakpointChange);
        } else {
          this._stickyMobileQuery.removeListener(this._stickyHeroBreakpointChange);
        }
      }
      this._stickyMobileQuery = null;
      this._stickyHeroSection = null;
    }

    initStickyMobileHero() {
      const section = this.closest(".m-slider--fixed-hero");
      const image = section && section.querySelector(".m-fixed-hero-mobile__image");
      if (!image || !window.CSS ||
          !CSS.supports("position", "sticky") || !CSS.supports("overflow", "clip")) return;

      this._stickyHeroSection = section;
      this._stickyHeroImage = image;
      this._stickyMobileQuery = window.matchMedia("(max-width: 767px)");
      this._stickyHeroReady = () => {
        if (!this.isConnected || !this._stickyMobileQuery.matches || !image.naturalWidth) return;
        const media = this.querySelector(".m-slide__media");
        if (!media) return;
        const mediaHeight = media.getBoundingClientRect().height;
        if (mediaHeight < 2) return; // Do not hide the working fallback.
        // Measure ONCE when the image is ready, not on scroll or Safari toolbar resize.
        const heroDocumentTop =
        section.getBoundingClientRect().top +
        (window.scrollY || document.documentElement.scrollTop || 0);

      const startOffset = Math.max(
        0,
        Math.min(240, Math.round(heroDocumentTop))
      );

      section.style.setProperty(
        "--m-sticky-start-offset",
        `${startOffset}px`
      );
        section.style.setProperty("--m-sticky-mobile-height", `${Math.ceil(mediaHeight)}px`);
        section.classList.add("m-slider--sticky-ready");
      };
      image.addEventListener("load", this._stickyHeroReady);
      this._stickyHeroBreakpointChange = () => {
        if (this._stickyMobileQuery.matches) {
          requestAnimationFrame(this._stickyHeroReady);
        } else {
          section.classList.remove("m-slider--sticky-ready");
          section.style.removeProperty("--m-sticky-mobile-height");
        }
      };
      if (this._stickyMobileQuery.addEventListener) {
        this._stickyMobileQuery.addEventListener("change", this._stickyHeroBreakpointChange);
      } else {
        this._stickyMobileQuery.addListener(this._stickyHeroBreakpointChange);
      }
      if (image.complete) requestAnimationFrame(this._stickyHeroReady);
    }

    initFixedHero() {
      const section = this.closest(".m-slider--fixed-hero");
      if (!section || !this.querySelector('.swiper-slide[data-slide-type="slider_item"] .m-slide__bg picture')) return;

      // Cache DOM references: no selector scans during scrolling.
      this._fixedHeroItems = Array.from(
        this.querySelectorAll('.swiper-slide[data-slide-type="slider_item"] .m-slide__bg picture')
      ).map((picture) => ({ picture, media: picture.closest(".m-slide__media") }))
        .filter((item) => item.media);

      // Motion-sensitive visitors retain the ordinary responsive image.
      this._fixedHeroReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
      this._fixedHeroMotionChange = () => {
        if (this._fixedHeroReducedMotion.matches) {
          this.querySelectorAll(".m-slide__bg picture").forEach((picture) => {
            picture.style.removeProperty("height");
            picture.style.removeProperty("transform");
          });
        } else {
          this._fixedHeroSchedule();
        }
      };
      this._fixedHeroDesktop = window.matchMedia("(min-width: 768px)");
      this._fixedHeroSchedule = () => {
        if (!this._fixedHeroDesktop.matches) return;
        if (this._fixedHeroFrame != null) return;
        this._fixedHeroFrame = requestAnimationFrame(() => {
          this._fixedHeroFrame = null;
          this.updateFixedHero();
        });
      };
      window.addEventListener("scroll", this._fixedHeroSchedule, { passive: true });
      window.addEventListener("resize", this._fixedHeroSchedule);
      window.addEventListener("orientationchange", this._fixedHeroSchedule);
      if (window.visualViewport) {
        window.visualViewport.addEventListener("resize", this._fixedHeroSchedule);
      }
      if (this._fixedHeroReducedMotion.addEventListener) {
        this._fixedHeroReducedMotion.addEventListener(
          "change", this._fixedHeroMotionChange
        );
      } else {
        this._fixedHeroReducedMotion.addListener(this._fixedHeroMotionChange);
      }
      this._fixedHeroSchedule();
    }

    updateFixedHero() {
      if (!this.isConnected || this._fixedHeroReducedMotion.matches || !this._fixedHeroDesktop.matches) return;
      const viewportHeight = window.innerHeight;
      const items = this._fixedHeroItems || [];
      // Read layout once, then update ONLY the compositor transform.
      // NEVER change the image's height while scrolling: Safari's collapsing
      // browser toolbar would otherwise continuously change its crop.
      const visible = items.map(({ picture, media }) => ({
        picture, rect: media.getBoundingClientRect()
      })).filter(({ rect }) => rect.bottom > 0 && rect.top < viewportHeight);
      visible.forEach(({ picture, rect }) => {
        picture.style.transform = `translate3d(0, ${-rect.top}px, 0)`;
      });
    }

    initSlider() {
      this.slider = new MinimogLibs.Swiper(this.sliderContainer, {
        init: false,
        autoplay: this.autoplay
          ? {
              delay: this.autoplaySpeed * 1000,
              disableOnInteraction: true,
            }
          : false,
        slidesPerView: 1,
        slidesPerGroup: 1,
        loop: this.sliderContainer.querySelectorAll(".swiper-slide").length > 1,
        effect: "fade",
        fadeEffect: {
          crossFade: true,
        },
        speed: 1000,
        navigation: this.showArrows
          ? {
              nextEl: this.querySelector(".m-slider-controls__button-next"),
              prevEl: this.querySelector(".m-slider-controls__button-prev"),
            }
          : false,
        pagination: this.showDots
          ? {
              el: this.querySelector(".swiper-pagination"),
              clickable: true,
              bulletClass: "m-dot",
              bulletActiveClass: "m-dot--active",
            }
          : false,
        breakpoints: {
          992: {
            threshold: 2,
          },
        },
        on: {
          init() {
            setTimeout(() => {
              window.dispatchEvent(new Event("resize"));
            }, 100);
          },
        },
      });

      this.lastSlide = null;
      this.currentSlide = null;
      this.slider && this.slider.on("init", this.handleChange.bind(this));
      this.slider && this.slider.on("slideChange", this.handleChange.bind(this));
      this.slider.init();

      if (Shopify.designMode) {
        this._onBlockSelect = (e) => {
          if (this.contains(e.target)) this.onBlockSelect(e);
        };
        document.addEventListener("shopify:block:select", this._onBlockSelect);
      }
    }

    handleChange(swiper) {
      const { activeIndex, slides } = swiper;
      const slideType = slides[activeIndex] && slides[activeIndex].dataset.slideType;
      this.lastActive && this.slider.slideToLoop(this.lastActive);
      if (slideType === "video_slide") {
        const video = slides[activeIndex] && slides[activeIndex].querySelector("video");

        if (video) {
          let playPromise = video.play();
          this.lastVideo && this.lastVideo.pause();
          if (playPromise !== undefined) {
            playPromise.then(() => {
              this.lastVideo = video;
            });
          }
        }
      } else {
        this.lastVideo && this.lastVideo.pause();
        this.lastVideo = null;
      }
    }

    onBlockSelect(ev) {
      const block = ev.target;
      const index = Number(block.dataset.slide);
      this.lastActive = index;
      if (this.slider) {
        this.slider.slideToLoop(index);
      }
    }
  }

  customElements.define("m-slideshow", MSlideshow);
}
